<script lang="ts">
	import { Select } from "flowbite-svelte-v1";
	import Label from "#lib/components/Label.svelte";
	import P from "#lib/components/P.svelte";
	import ColorInputV2 from "#lib/components/flow/editor/appearance/ColorInputV2.svelte";
	import { BORDER_RADIUS_VALUES, BUTTON_SIZES, FULL_BORDER_RADIUS } from "#lib/components/flow/editor/appearance/constants.js";
	import { camelCase } from "#lib/string.js";
	import type { JsonValue } from "@bufbuild/protobuf";

	interface Props {
		prefix?: string;
		defaults?: Record<string, JsonValue>;
		values: Record<string, JsonValue>;
		onInput?: (key: string, value: JsonValue) => void;
		onChange?: (key: string, value: JsonValue) => void;
		onReset?: (key: string) => void;
	}

	let { prefix = "", defaults = {}, values, onInput, onChange, onReset }: Props = $props();

	const resolvedBorderRadiusValues = $derived(BORDER_RADIUS_VALUES.concat({ name: "Full", value: FULL_BORDER_RADIUS }));

	const buttonSizeId = $derived(camelCase(prefix + "ButtonSize"));
	const borderRadiusId = $derived(camelCase(prefix + "ButtonBorderRadius"));
	const backgroundColorId = $derived(camelCase(prefix + "ButtonBackgroundColor"));
	const borderColorId = $derived(camelCase(prefix + "ButtonBorderColor"));
	const textColorId = $derived(camelCase(prefix + "ButtonTextColor"));

	function handleChangeAppearance(e: Event, key: string) {
		const value = (e.target as HTMLSelectElement).value;
		onChange?.(key, value);
	}

	function handleColorInput(key: string, value: JsonValue) {
		onInput?.(key, value);
	}

	function resetToPageStyle(key: string) {
		onReset?.(key);
	}
</script>

<div class="flex flex-col gap-2">
	<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Button</P>
	<div class="grid grid-cols-2 gap-x-4 gap-y-3 border-l-[5px] border-l-gray-300 pl-6">
		<div class="flex flex-col gap-1">
			<Label for={buttonSizeId} class="font-medium">Button size</Label>
			<Select classes={{ select: "truncate" }} items={BUTTON_SIZES} value={values[buttonSizeId] ?? defaults[buttonSizeId]} aria-labelledby={buttonSizeId} onchange={e => handleChangeAppearance(e, buttonSizeId)} />
		</div>
		<div class="flex flex-col gap-1">
			<Label for={borderRadiusId} class="font-medium">Border radius</Label>
			<Select classes={{ select: "truncate" }} items={resolvedBorderRadiusValues} value={values[borderRadiusId] ?? defaults[borderRadiusId]} aria-labelledby={borderRadiusId} onchange={e => handleChangeAppearance(e, borderRadiusId)} />
		</div>
		<div class="flex flex-col gap-1">
			<Label for={backgroundColorId} class="font-medium">Background color</Label>
			<ColorInputV2 id={backgroundColorId} allowTransparent resetColor={defaults[backgroundColorId] as string} value={(values[backgroundColorId] as string) ?? defaults[backgroundColorId]} display="inline" onReset={() => resetToPageStyle(backgroundColorId)} oninput={value => handleColorInput(backgroundColorId, value)} onchange={value => onChange?.(backgroundColorId, value)} />
		</div>
		<div class="flex flex-col gap-1">
			<Label for={borderColorId} class="font-medium">Border color</Label>
			<ColorInputV2 id={borderColorId} resetColor={defaults[borderColorId] as string} display="inline" value={(values[borderColorId] as string) ?? defaults[borderColorId]} oninput={value => handleColorInput(borderColorId, value)} onchange={value => onChange?.(borderColorId, value)} />
		</div>
	</div>
</div>
<div class="flex flex-col gap-2">
	<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Text</P>
	<div class="grid grid-cols-2 gap-x-4 gap-y-3 border-l-[5px] border-l-gray-300 pl-6">
		<div class="col-span-2 flex flex-col gap-1">
			<Label for={textColorId} class="font-medium">Text color</Label>
			<ColorInputV2 id={textColorId} resetColor={defaults[textColorId] as string} display="inline" value={(values[textColorId] as string) ?? defaults[textColorId]} onReset={() => resetToPageStyle(textColorId)} oninput={value => handleColorInput(textColorId, value)} onchange={value => onChange?.(textColorId, value)} />
		</div>
	</div>
</div>
